import { EmailChipsInput } from '@/components/ui/EmailChipsInput';
import { TimePicker } from '@/components/ui/TimePicker';
import { EditionOption, editionsService } from '@/services/editions.service';
import { useAuthStore } from '@/store/auth.store';
import {
  normalizeEmailFromArray,
  normalizeEmailToArray,
} from '@/utils/email.utils';
import {
  convertParisTimeToUserTimezone,
  getUserTimezone,
} from '@/utils/timezone';
import { Calendar, Clock, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditionConfig } from '../types/automation.types';

interface EditionManagerProps {
  folderUuid: string;
  editions: EditionConfig[];
  onEditionsChange: (editions: EditionConfig[]) => void;
  isLoading?: boolean;
  error?: string | null;
  defaultEmail?: string;
}

export const EditionManager = ({
  folderUuid,
  editions,
  onEditionsChange,
  isLoading = false,
  error = null,
  defaultEmail = '',
}: EditionManagerProps) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [editionOptions, setEditionOptions] = useState<EditionOption[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  // Fetch edition options when component mounts
  useEffect(() => {
    if (folderUuid) {
      setIsLoadingOptions(true);
      setOptionsError(null);
      editionsService
        .getFolderOptions(folderUuid)
        .then(resp => {
          setEditionOptions(resp.data.options);
        })
        .catch((err: unknown) => {
          const message =
            (err as HttpApiError)?.response?.data?.message ||
            (err as HttpApiError)?.message ||
            'Failed to load edition options';
          setOptionsError(message);
        })
        .finally(() => {
          setIsLoadingOptions(false);
        });
    }
  }, [folderUuid]);

  const addEdition = () => {
    const newEdition: EditionConfig = {
      id: `edition-${Date.now()}-${Math.random()}`,
      editionOptionValue: '',
      editionOptionName: '',
      editionEmail: defaultEmail,
      dayOfMonth: 1,
      executionHour: 10,
      executionMinute: 0,
      usePreviousMonth: true,
    };
    onEditionsChange([...editions, newEdition]);
  };

  const removeEdition = (id: string) => {
    onEditionsChange(editions.filter(edition => edition.id !== id));
  };

  const updateEdition = (
    id: string,
    field: keyof EditionConfig,
    value: string | number | boolean
  ) => {
    onEditionsChange(
      editions.map(edition =>
        edition.id === id ? { ...edition, [field]: value } : edition
      )
    );
  };

  const handleEditionOptionChange = (id: string, value: string) => {
    const selectedOption = editionOptions.find(opt => opt.value === value);
    onEditionsChange(
      editions.map(edition =>
        edition.id === id
          ? {
              ...edition,
              editionOptionValue: value,
              editionOptionName: selectedOption?.name || '',
            }
          : edition
      )
    );
  };

  if (isLoading || isLoadingOptions) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="text-sm text-neutral-500">
          {t('common.loading', 'Loading...')}
        </div>
      </div>
    );
  }

  if (error || optionsError) {
    return (
      <div className="bg-error-50 rounded-lg p-4">
        <div className="text-error-600 text-sm">{error || optionsError}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-neutral-900">
          {t('automation.edition.multiple.title', 'Edition Configurations')}
        </h4>
        <button
          onClick={addEdition}
          disabled={editionOptions.length === 0}
          className="bg-primary-600 hover:bg-primary-700 flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-3 w-3" />
          {t('automation.edition.add', 'Add Edition')}
        </button>
      </div>

      {editions.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-neutral-300 p-6 text-center">
          <div className="text-sm text-neutral-500">
            {t('automation.edition.empty', 'No editions configured')}
          </div>
          <button
            onClick={addEdition}
            disabled={editionOptions.length === 0}
            className="text-primary-600 hover:text-primary-700 mt-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('automation.edition.addFirst', 'Add your first edition')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {editions.map((edition, index) => (
            <div
              key={edition.id}
              className="rounded-lg border border-neutral-200 bg-neutral-50 p-4"
            >
              <div className="mb-3 flex items-center justify-between">
                <h5 className="text-sm font-medium text-neutral-900">
                  {t(
                    'automation.edition.configuration',
                    'Edition Configuration'
                  )}{' '}
                  #{index + 1}
                </h5>
                {editions.length > 1 && (
                  <button
                    onClick={() => removeEdition(edition.id)}
                    className="flex h-6 w-6 items-center justify-center rounded text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>

              <div className="space-y-3">
                {/* Edition Option Selection */}
                <div>
                  <label className="mb-1 block text-xs font-medium text-neutral-700">
                    {t('automation.edition.option', 'Edition Option')}
                  </label>
                  <select
                    value={edition.editionOptionValue}
                    onChange={e =>
                      handleEditionOptionChange(edition.id, e.target.value)
                    }
                    className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:ring-2"
                  >
                    <option value="">
                      {t(
                        'automation.edition.selectPlaceholder',
                        'Select an edition'
                      )}
                    </option>
                    {editionOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Edition Email */}
                <div>
                  <EmailChipsInput
                    emails={normalizeEmailToArray(edition.editionEmail)}
                    onChange={emails =>
                      updateEdition(
                        edition.id,
                        'editionEmail',
                        normalizeEmailFromArray(emails)
                      )
                    }
                    label={t(
                      'automation.edition.email',
                      'Edition PDF email (optional)'
                    )}
                    placeholder="editions@example.com"
                    description={t(
                      'automation.edition.emailDescription',
                      "Destinataires du PDF d'édition (plusieurs emails possibles)"
                    )}
                  />
                </div>

                {/* Month Selection Switch */}
                <div>
                  <label className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-neutral-700">
                      {t('automation.edition.month', 'Month Selection')}
                    </span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs ${
                          !edition.usePreviousMonth
                            ? 'font-medium text-neutral-900'
                            : 'text-neutral-500'
                        }`}
                      >
                        {t('automation.edition.currentMonth', 'Current Month')}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          updateEdition(
                            edition.id,
                            'usePreviousMonth',
                            !edition.usePreviousMonth
                          )
                        }
                        className={`focus:ring-primary-500 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none ${
                          edition.usePreviousMonth
                            ? 'bg-primary-600'
                            : 'bg-neutral-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            edition.usePreviousMonth
                              ? 'translate-x-6'
                              : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <span
                        className={`text-xs ${
                          edition.usePreviousMonth
                            ? 'font-medium text-neutral-900'
                            : 'text-neutral-500'
                        }`}
                      >
                        {t(
                          'automation.edition.previousMonth',
                          'Previous Month (M-1)'
                        )}
                      </span>
                    </div>
                  </label>
                  <p className="text-xs text-neutral-500">
                    {t(
                      'automation.edition.monthDescription',
                      'Choose whether to generate the edition for the current month or the previous month'
                    )}
                  </p>
                </div>

                {/* Day of Month */}
                <div>
                  <label className="mb-1 block text-xs font-medium text-neutral-700">
                    <Calendar className="mr-1 inline h-3 w-3" />
                    {t('automation.dayOfMonth', 'Day of Month')}
                  </label>
                  <select
                    value={edition.dayOfMonth}
                    onChange={e =>
                      updateEdition(
                        edition.id,
                        'dayOfMonth',
                        parseInt(e.target.value)
                      )
                    }
                    className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:ring-2"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Execution Time */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Clock className="h-3 w-3 text-neutral-400" />
                    <span className="text-xs font-medium text-neutral-700">
                      {t(
                        'automation.executionTime.title',
                        "Heures d'exécution"
                      )}
                    </span>
                  </div>
                  <p className="text-xs text-neutral-500">
                    {t(
                      'automation.executionTime.timezoneExplanation',
                      "Les heures sont définies en timezone Paris (Europe/Paris). L'équivalent dans votre timezone locale sera affiché ci-dessous."
                    )}
                  </p>

                  <TimePicker
                    hour={edition.executionHour}
                    minute={edition.executionMinute}
                    onHourChange={hour =>
                      updateEdition(edition.id, 'executionHour', hour)
                    }
                    onMinuteChange={minute =>
                      updateEdition(edition.id, 'executionMinute', minute)
                    }
                    label={t(
                      'automation.executionTime.executionTime',
                      "Heure d'exécution"
                    )}
                  />

                  {/* Timezone equivalent display */}
                  <div className="bg-primary-50 hidden rounded-lg p-2">
                    <div className="flex items-center gap-2 text-xs">
                      <Clock className="text-primary-600 h-3 w-3" />
                      <span className="text-primary-900 font-medium">
                        {t(
                          'automation.executionTime.equivalentInYourTimezone',
                          'Équivalent dans votre timezone:'
                        )}
                      </span>
                    </div>
                    <div className="text-primary-700 mt-1 text-xs">
                      {(() => {
                        const userTimezone = getUserTimezone(user?.timezone);
                        const timeConversion = convertParisTimeToUserTimezone(
                          edition.executionHour,
                          edition.executionMinute,
                          userTimezone
                        );
                        return timeConversion.displayText;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editionOptions.length === 0 && (
        <div className="bg-warning-50 rounded-lg p-3">
          <div className="text-warning-700 text-sm">
            {t(
              'automation.edition.noOptions',
              'No edition options available for this folder'
            )}
          </div>
        </div>
      )}
    </div>
  );
};
